import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FileText, Plus, Edit3, Trash2, Copy, ArrowUp, ArrowDown, Save, X, GripVertical, Eye } from 'lucide-react';
import { theme, cardStyle, btnStyle, inputStyle } from '../theme';

// ═══════════════════════════════════════════════════════════════════
//  FORM BUILDER — admin creates custom intake/consent forms.
// ═══════════════════════════════════════════════════════════════════

const FIELD_TYPES = [
  { type: 'text',      label: 'Text',       icon: 'T' },
  { type: 'textarea',  label: 'Paragraph',  icon: '¶' },
  { type: 'number',    label: 'Number',     icon: '#' },
  { type: 'email',     label: 'Email',      icon: '@' },
  { type: 'phone',     label: 'Phone',      icon: '📞' },
  { type: 'date',      label: 'Date',       icon: '📅' },
  { type: 'select',    label: 'Dropdown',   icon: '▾' },
  { type: 'radio',     label: 'Radio',      icon: '⦿' },
  { type: 'checkbox',  label: 'Checkboxes', icon: '☑' },
  { type: 'rating',    label: 'Rating',     icon: '★' },
  { type: 'signature', label: 'Signature',  icon: '✍' },
  { type: 'section',   label: 'Section',    icon: '§' },
  { type: 'divider',   label: 'Divider',    icon: '—' },
];

const CATEGORIES = [
  { key: 'intake',    label: 'Intake' },
  { key: 'consent',   label: 'Consent' },
  { key: 'feedback',  label: 'Feedback' },
  { key: 'screening', label: 'Screening' },
  { key: 'followup',  label: 'Follow-up' },
  { key: 'other',     label: 'Other' },
];

const genId = () => 'f_' + Math.random().toString(36).slice(2, 9);

const FormBuilderPage = ({ API_BASE, currentUser }) => {
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null);                              // template in edit mode
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [msg, setMsg] = useState({ type: '', text: '' });
  const showMsg = (t, text) => { setMsg({ type: t, text }); setTimeout(() => setMsg({ type: '', text: '' }), 3500); };

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await axios.get(`${API_BASE}/forms/templates?active=true`); setTemplates(r.data || []); }
    catch { showMsg('error', 'Load failed'); }
    finally { setLoading(false); }
  }, [API_BASE]);

  useEffect(() => { load(); }, [load]);

  const newTemplate = () => setEditing({
    name: '', category: 'intake', description: '', fields: [],
    active: true, requireEsign: false, autoAssign: false,
  });

  const save = async () => {
    try {
      if (editing._id) {
        await axios.put(`${API_BASE}/forms/templates/${editing._id}`, editing);
      } else {
        await axios.post(`${API_BASE}/forms/templates`, editing);
      }
      showMsg('success', '✓ Saved');
      setEditing(null); load();
    } catch (e) { showMsg('error', 'Save failed: ' + (e.response?.data?.error || e.message)); }
  };

  const del = async (t) => {
    if (!window.confirm(`Archive "${t.name}"?`)) return;
    await axios.delete(`${API_BASE}/forms/templates/${t._id}`);
    load();
  };

  const dup = (t) => {
    setEditing({ ...t, _id: undefined, name: t.name + ' (copy)', fields: t.fields.map(f => ({ ...f, id: genId() })) });
  };

  // Field ops within editor
  const addField = (type) => {
    const f = {
      id: genId(), type, label: FIELD_TYPES.find(x => x.type === type).label,
      required: false, options: [], order: editing.fields.length,
    };
    if (['select', 'radio', 'checkbox'].includes(type)) f.options = ['Option 1', 'Option 2'];
    setEditing({ ...editing, fields: [...editing.fields, f] });
  };
  const updField = (id, patch) => setEditing({ ...editing, fields: editing.fields.map(f => f.id === id ? { ...f, ...patch } : f) });
  const delField = (id) => setEditing({ ...editing, fields: editing.fields.filter(f => f.id !== id) });
  const moveField = (idx, dir) => {
    const fs = [...editing.fields];
    const target = idx + dir;
    if (target < 0 || target >= fs.length) return;
    [fs[idx], fs[target]] = [fs[target], fs[idx]];
    setEditing({ ...editing, fields: fs.map((f, i) => ({ ...f, order: i })) });
  };

  return (
    <div>
      <div style={{ ...cardStyle, marginBottom: 14, background: `linear-gradient(135deg, ${theme.primary}, ${theme.primaryDark})`, color: 'white', border: 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h2 style={{ margin: 0, color: 'white', fontSize: '1.4rem' }}><FileText size={22} style={{ verticalAlign: -4, marginRight: 6 }} /> Custom Forms</h2>
            <p style={{ margin: '4px 0 0', opacity: 0.9, fontSize: 13 }}>{templates.length} templates · intake / consent / feedback forms</p>
          </div>
          {!editing && (
            <button onClick={newTemplate} style={headBtn}><Plus size={14} style={{ marginRight: 4 }} /> New Form</button>
          )}
        </div>
      </div>

      {msg.text && (
        <div style={{ padding: '10px 14px', marginBottom: 12, borderRadius: 8,
          background: msg.type === 'error' ? '#fee' : '#efe',
          color: msg.type === 'error' ? theme.danger : theme.success,
          border: `1px solid ${msg.type === 'error' ? theme.danger : theme.success}44`, fontWeight: 600, fontSize: 13.5 }}>{msg.text}</div>
      )}

      {editing ? (
        <FormEditor
          editing={editing}
          setEditing={setEditing}
          addField={addField}
          updField={updField}
          delField={delField}
          moveField={moveField}
          save={save}
          onCancel={() => setEditing(null)}
          onPreview={() => setPreview(editing)}
        />
      ) : loading ? (
        <div style={{ ...cardStyle, textAlign: 'center', padding: 40 }}>Loading…</div>
      ) : templates.length === 0 ? (
        <div style={{ ...cardStyle, textAlign: 'center', padding: 60 }}>
          <FileText size={44} color={theme.textSubtle} style={{ marginBottom: 8, opacity: 0.5 }} />
          <p style={{ color: theme.textMuted, fontWeight: 600 }}>Koi form template nahi</p>
          <button onClick={newTemplate} style={{ ...btnStyle, marginTop: 10 }}><Plus size={14} style={{ marginRight: 6 }} /> Create First Form</button>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10 }}>
          {templates.map(t => (
            <div key={t._id} style={{ ...cardStyle, padding: 14 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: theme.primaryDark, background: theme.primary + '18', padding: '2px 7px', borderRadius: 4 }}>{t.category}</span>
                  <div style={{ fontWeight: 700, fontSize: 14.5, color: theme.navy, marginTop: 4 }}>{t.name}</div>
                  {t.description && <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 2 }}>{t.description}</div>}
                  <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 6 }}>
                    {t.fields.length} fields{t.requireEsign && ' · ✍ e-sign'}{t.autoAssign && ' · auto-assign'}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 4, marginTop: 10 }}>
                <button onClick={() => setPreview(t)} style={smallBtn}><Eye size={11} /> Preview</button>
                <button onClick={() => setEditing(t)} style={smallBtn}><Edit3 size={11} /> Edit</button>
                <button onClick={() => dup(t)} style={smallBtn}><Copy size={11} /> Duplicate</button>
                <button onClick={() => del(t)} style={{ ...smallBtn, color: theme.danger, borderColor: theme.danger + '33' }}><Trash2 size={11} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {preview && <PreviewModal template={preview} onClose={() => setPreview(null)} />}
    </div>
  );
};

// ── Form Editor component ──
const FormEditor = ({ editing, setEditing, addField, updField, delField, moveField, save, onCancel, onPreview }) => (
  <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 12 }}>
    <div style={{ ...cardStyle, padding: 10, position: 'sticky', top: 10, alignSelf: 'start' }}>
      <div style={{ fontSize: 10.5, fontWeight: 700, color: theme.textMuted, textTransform: 'uppercase', marginBottom: 6 }}>Add Field</div>
      {FIELD_TYPES.map(t => (
        <button key={t.type} onClick={() => addField(t.type)} style={{ display: 'block', width: '100%', padding: '7px 10px', marginBottom: 3, border: `1px solid ${theme.border}`, borderRadius: 6, background: 'white', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: theme.navy, textAlign: 'left' }}>
          <span style={{ width: 22, display: 'inline-block', color: theme.primary, fontWeight: 900 }}>{t.icon}</span>{t.label}
        </button>
      ))}
    </div>

    <div>
      <div style={{ ...cardStyle, padding: 14, marginBottom: 10 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 8, marginBottom: 8 }}>
          <input placeholder="Form name" value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} style={{ ...inputStyle, fontSize: 15, fontWeight: 700 }} />
          <select value={editing.category} onChange={e => setEditing({ ...editing, category: e.target.value })} style={inputStyle}>
            {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
        </div>
        <input placeholder="Short description (optional)" value={editing.description || ''} onChange={e => setEditing({ ...editing, description: e.target.value })} style={{ ...inputStyle, marginBottom: 8 }} />
        <div style={{ display: 'flex', gap: 14, fontSize: 12.5 }}>
          <label><input type="checkbox" checked={editing.requireEsign} onChange={e => setEditing({ ...editing, requireEsign: e.target.checked })} /> Require e-sign</label>
          <label><input type="checkbox" checked={editing.autoAssign} onChange={e => setEditing({ ...editing, autoAssign: e.target.checked })} /> Auto-attach to new patients</label>
        </div>
      </div>

      {editing.fields.length === 0 ? (
        <div style={{ ...cardStyle, padding: 40, textAlign: 'center', color: theme.textMuted, border: `2px dashed ${theme.border}` }}>
          ← Side se field type select karo
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 6 }}>
          {editing.fields.map((f, idx) => (
            <div key={f.id} style={{ ...cardStyle, padding: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                <GripVertical size={14} color={theme.textMuted} />
                <span style={{ fontSize: 10, fontWeight: 700, color: theme.primaryDark, background: theme.primary + '18', padding: '2px 7px', borderRadius: 4, textTransform: 'uppercase' }}>{f.type}</span>
                <input value={f.label} onChange={e => updField(f.id, { label: e.target.value })}
                  placeholder="Field label" style={{ ...inputStyle, flex: 1, fontSize: 13, fontWeight: 600 }} />
                <button onClick={() => moveField(idx, -1)} disabled={idx === 0} style={smallBtn}><ArrowUp size={11} /></button>
                <button onClick={() => moveField(idx, 1)} disabled={idx === editing.fields.length - 1} style={smallBtn}><ArrowDown size={11} /></button>
                <button onClick={() => delField(f.id)} style={{ ...smallBtn, color: theme.danger, borderColor: theme.danger + '33' }}><Trash2 size={11} /></button>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', paddingLeft: 22 }}>
                <label style={{ fontSize: 12 }}>
                  <input type="checkbox" checked={f.required} onChange={e => updField(f.id, { required: e.target.checked })} /> Required
                </label>
                {['text', 'textarea', 'number', 'email', 'phone'].includes(f.type) && (
                  <input placeholder="Placeholder" value={f.placeholder || ''} onChange={e => updField(f.id, { placeholder: e.target.value })}
                    style={{ ...inputStyle, fontSize: 12, padding: '4px 8px', flex: 1, minWidth: 140 }} />
                )}
                {['select', 'radio', 'checkbox'].includes(f.type) && (
                  <input placeholder="Options (comma-separated)" value={(f.options || []).join(', ')}
                    onChange={e => updField(f.id, { options: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
                    style={{ ...inputStyle, fontSize: 12, padding: '4px 8px', flex: 1, minWidth: 180 }} />
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        <button onClick={save} disabled={!editing.name} style={btnStyle}><Save size={13} style={{ marginRight: 6 }} /> Save</button>
        <button onClick={onPreview} style={{ ...btnStyle, background: '#64748b' }}><Eye size={13} style={{ marginRight: 6 }} /> Preview</button>
        <button onClick={onCancel} style={{ ...btnStyle, background: 'white', color: theme.navy, border: `1px solid ${theme.border}` }}><X size={13} style={{ marginRight: 6 }} /> Cancel</button>
      </div>
    </div>
  </div>
);

// ── Preview modal (renders form as patient would see it) ──
const PreviewModal = ({ template, onClose }) => (
  <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }} onClick={onClose}>
    <div style={{ background: 'white', borderRadius: 12, padding: 22, width: '100%', maxWidth: 540, maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <h3 style={{ margin: 0 }}>{template.name}</h3>
        <button onClick={onClose} style={smallBtn}><X size={14} /></button>
      </div>
      {template.description && <p style={{ fontSize: 13, color: theme.textMuted, margin: '0 0 12px' }}>{template.description}</p>}
      <div style={{ display: 'grid', gap: 10 }}>
        {template.fields.map(f => {
          if (f.type === 'section') return <div key={f.id} style={{ fontSize: 14, fontWeight: 700, color: theme.primaryDark, marginTop: 6 }}>{f.label}</div>;
          if (f.type === 'divider') return <hr key={f.id} style={{ border: 'none', borderTop: `1px solid ${theme.border}`, margin: '4px 0' }} />;
          return (
            <div key={f.id}>
              <label style={{ fontSize: 12.5, fontWeight: 600, color: theme.navy, display: 'block', marginBottom: 3 }}>
                {f.label}{f.required && <span style={{ color: theme.danger }}>*</span>}
              </label>
              {f.type === 'textarea' ? <textarea placeholder={f.placeholder} rows={3} style={{ ...inputStyle, width: '100%', fontFamily: 'inherit' }} /> :
               f.type === 'select' ? <select style={{ ...inputStyle, width: '100%' }}><option>Select…</option>{(f.options || []).map(o => <option key={o}>{o}</option>)}</select> :
               f.type === 'radio' ? (f.options || []).map(o => <label key={o} style={{ display: 'block', fontSize: 13, marginBottom: 3 }}><input type="radio" name={f.id} /> {o}</label>) :
               f.type === 'checkbox' ? (f.options || []).map(o => <label key={o} style={{ display: 'block', fontSize: 13, marginBottom: 3 }}><input type="checkbox" /> {o}</label>) :
               f.type === 'rating' ? <div style={{ fontSize: 22, color: '#e2e8f0' }}>★★★★★</div> :
               f.type === 'signature' ? <div style={{ border: `2px dashed ${theme.border}`, borderRadius: 6, padding: 20, textAlign: 'center', fontSize: 12, color: theme.textMuted }}>Signature area</div> :
               <input type={f.type} placeholder={f.placeholder} style={{ ...inputStyle, width: '100%' }} />}
            </div>
          );
        })}
        {template.requireEsign && (
          <div style={{ marginTop: 8, padding: 10, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, fontSize: 12, color: '#92400e' }}>
            ✍ E-signature required
          </div>
        )}
      </div>
    </div>
  </div>
);

const smallBtn = { display: 'inline-flex', alignItems: 'center', gap: 3, padding: '4px 9px', borderRadius: 5, border: `1px solid ${theme.border}`, background: 'white', color: theme.navy, cursor: 'pointer', fontSize: 11, fontWeight: 600 };
const headBtn = { ...btnStyle, marginTop: 0, background: 'rgba(255,255,255,0.18)', color: 'white', border: '1px solid rgba(255,255,255,0.4)' };

export default FormBuilderPage;
